import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { ArrowLeft, Mail, MessageCircle, Phone, MapPin, Clock, ShieldCheck } from "lucide-react";

// ---------------------------------------------------------------------------
// Contact Us — required by Cashfree KYC + Apple/Google submissions. Lists
// founder/operations contact details for the platform.
// ---------------------------------------------------------------------------
const CONTACT = {
  email:    "support@neuroopinion.in",
  whatsapp: "918317674160",
  whatsappDisplay: "+91 83176 74160",
  phone:    "+91 83176 74160",
  assistance: "+91 94417 24072",
  hours:    "Mon–Sat · 9:00 AM – 9:00 PM IST",
  // TODO: update with your registered office once Cashfree KYC is complete
  address: "NeuroOpinion India\nHyderabad, Telangana 500001\nIndia",
};

export default function Contact() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
          Contact NeuroOpinion India
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          We&apos;re here to help with second opinions, scan uploads, payments,
          or anything you couldn&apos;t resolve in the app. Most questions are
          answered within a few hours.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <ContactCard icon={MessageCircle} accent="text-green-600 bg-green-50 border-green-200" label="WhatsApp (fastest)" value={CONTACT.whatsappDisplay}
            href={`https://wa.me/${CONTACT.whatsapp}`} note="Mon–Sat · usually replies within 30 min" />

          <ContactCard icon={Mail} accent="text-[#0B5ED7] bg-[#0B5ED7]/10 border-[#0B5ED7]/20" label="Email" value={CONTACT.email}
            href={`mailto:${CONTACT.email}`} note="Reply within 24 hours" />

          <ContactCard icon={Phone} accent="text-slate-700 bg-slate-100 border-slate-200" label="Phone (Primary)" value={CONTACT.phone}
            href={`tel:${CONTACT.phone.replace(/\s+/g, "")}`} note={CONTACT.hours} />

          <ContactCard icon={Phone} accent="text-purple-700 bg-purple-50 border-purple-200" label="Assistance" value={CONTACT.assistance}
            href={`tel:${CONTACT.assistance.replace(/\s+/g, "")}`} note="For general help & queries" />

          <ContactCard icon={Clock} accent="text-amber-700 bg-amber-50 border-amber-200" label="Working hours" value={CONTACT.hours}
            note="Priority cases reviewed within 24 hours, even on holidays" />
        </div>

        <Card className="mt-8 p-6 border-slate-200">
          <div className="inline-flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center text-slate-700">
              <MapPin className="w-4 h-4"/>
            </div>
            <h2 className="font-heading font-semibold text-slate-900 text-lg">Registered office</h2>
          </div>
          <pre className="mt-3 pl-10 text-sm text-slate-700 whitespace-pre-line font-sans leading-relaxed">{CONTACT.address}</pre>
          <p className="mt-3 pl-10 text-xs text-slate-500">
            Mailing address only — patient consultations are conducted entirely online.
            For in-person referrals, our specialists practise at affiliated NABH-accredited hospitals across India.
          </p>
        </Card>

        <div className="mt-8 inline-flex items-center gap-2 text-sm text-slate-600">
          <ShieldCheck className="w-4 h-4 text-[#20C997]"/>
          We never ask for cash, UPI transfers outside the platform, or full medical history over phone.
          Always verify our payment requests via the in-app order screen before paying.
        </div>

        <p className="mt-10 text-xs text-slate-400">Last updated: 22 February 2026</p>
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, href, note, accent }) {
  const content = (
    <Card className="p-5 border-slate-200 hover:shadow-md hover:border-slate-300 transition h-full">
      <div className={`w-10 h-10 rounded-lg border grid place-items-center ${accent}`}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-1 font-heading font-semibold text-slate-900 break-all">{value}</div>
      {note && <div className="mt-1.5 text-xs text-slate-500">{note}</div>}
    </Card>
  );
  if (!href) return content;
  return <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{content}</a>;
}
